import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { authAPI } from '../services/api';
import AuthCard from './AuthCard';
import styles from './auth.module.css';

const Login = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowResend(false);
    setResendSuccess(false);
    setLoading(true);

    const result = await login(usernameOrEmail, password);

    if (result.success) {
      navigate('/todos');
    } else {
      setError(result.error);
      if (result.error?.toLowerCase().includes('verify your email')) {
        setShowResend(true);
      }
    }

    setLoading(false);
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await authAPI.resendVerification(usernameOrEmail);
      setResendSuccess(true);
      setShowResend(false);
    } catch (err) {
      setError(err.response?.data?.message || t('auth.resendFailed'));
    }
    setResendLoading(false);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box className={styles.wrap}>
        <AuthCard>
          <Box className={styles.logoBox}>
            <img src="/static/taskchecker-logo.svg" alt={t('auth.logoAlt')} className={styles.logo} />
          </Box>

          {resendSuccess && (
            <Alert severity="success" className={styles.alert}>
              {t('auth.verificationEmailSent')}
            </Alert>
          )}

          {error && (
            <Alert severity="error" className={styles.alert}>
              {error}
              {showResend && (
                <Box className={styles.resendRow}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={handleResend}
                    disabled={resendLoading}
                  >
                    {resendLoading ? t('auth.resendSending') : t('auth.resendVerification')}
                  </Button>
                </Box>
              )}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} className={styles.formBox}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="usernameOrEmail"
              label={t('auth.usernameOrEmail')}
              name="usernameOrEmail"
              autoComplete="username"
              autoFocus
              value={usernameOrEmail}
              onChange={(e) => setUsernameOrEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('auth.password')}
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Box className={`${styles.linkRowRight} ${styles.mt1}`}>
              <Link to="/forgot-password" className={styles.link}>
                <Typography variant="body2" color="primary">
                  {t('auth.forgotPasswordLink')}
                </Typography>
              </Link>
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              className={styles.submitBtn}
              disabled={loading}
            >
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
            <Box className={`${styles.linkRowCenter} ${styles.mt2}`}>
              <Link to="/register" className={styles.link}>
                <Typography variant="body2" color="primary">
                  {t('auth.noAccount')}
                </Typography>
              </Link>
            </Box>
          </Box>
        </AuthCard>
      </Box>
    </Container>
  );
};

export default Login;
